import { marked } from 'marked';
import markedKatex from 'marked-katex-extension';
import katex from 'katex';
const blockRule = /^[ \t]*(\$\$)((?:\\[^]|[^\\])+?)\1[ \t]*(?:\n|$)/;
const startRule = /(?:^|\n)[ \t]*\$\$/;
/**
 * Replaces the bundled blockKatex tokenizer with one that also accepts
 * `$$…$$` with content on the delimiter lines, and tolerates indentation.
 */
function looseBlockKatex(options = {}) {
    return {
        extensions: [
            {
                name: 'blockKatex',
                level: 'block',
                start(src) {
                    return src.match(startRule)?.index;
                },
                tokenizer(src) {
                    const match = src.match(blockRule);
                    if (!match)
                        return;
                    return {
                        type: 'blockKatex',
                        raw: match[0],
                        text: match[2].trim(),
                        displayMode: true,
                    };
                },
                renderer(token) {
                    return katex.renderToString(token.text, { ...options, displayMode: true }) + '\n';
                },
            },
        ],
    };
}
marked.use(markedKatex({ throwOnError: false, nonStandard: true }));
marked.use(looseBlockKatex({ throwOnError: false })); // must come second
// ── Custom marked renderer: open all links in a new tab ─────────────────────
// Note: @types/marked@5 uses the legacy (href, title, text) signature.
marked.use({
    renderer: {
        link(href, title, text) {
            if (!href)
                return text;
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
        },
    },
});
/**
 * Extracts the renderable text from a list of message blocks, ignoring any
 * non-text blocks (e.g. `thinking` or tool-call blocks) that the assistant
 * may have produced. Concatenates all `text` blocks, if there are several.
 */
export function extractTextFromBlocks(blocks) {
    if (!blocks)
        return '';
    return blocks
        .filter((block) => block.block_type === 'text')
        .map((block) => block.text ?? '')
        .join('');
}
// ── Scroll helpers ───────────────────────────────────────────────────────────
/** Returns true when the element is scrolled to (or within 1 px of) its bottom. */
export function isScrolledToBottom(element) {
    return element.scrollHeight - element.scrollTop <= element.clientHeight + 1;
}
export function scrollToBottom(element) {
    element.scrollTop = element.scrollHeight;
}
// ── Avatar creation ──────────────────────────────────────────────────────────
function createAvatar(type) {
    const theme = getComputedStyle(document.documentElement).getPropertyValue('--my-theme').trim().replace(/"/g, '');
    const avatar = document.createElement('div');
    avatar.className = 'chat-image avatar';
    avatar.style.width = '2.5rem';
    avatar.style.height = '2.5rem';
    avatar.style.borderRadius = '9999px';
    avatar.style.backgroundSize = '80%';
    avatar.style.backgroundPosition = 'center';
    avatar.style.backgroundRepeat = 'no-repeat';
    const themeFolder = theme === 'dark' ? 'dark' : 'light';
    const iconName = type === 'user' ? 'magnifier' : `${window.__CONFIG__.product.toLowerCase()}_icon`;
    avatar.style.backgroundImage = `url('icons/${themeFolder}/${iconName}.png')`;
    return avatar;
}
// ── Message element factory ──────────────────────────────────────────────────
function createChatMessage(type, text) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `chat chat-${type === 'assistant' ? 'start' : 'end'}`;
    const chatBubble = document.createElement('div');
    chatBubble.className = `chat-bubble ${type === 'assistant' ? '' : 'chat-bubble-neutral'} prose prose-sm max-w-none`;
    chatBubble.innerHTML = marked.parse(text);
    messageDiv.appendChild(createAvatar(type));
    messageDiv.appendChild(chatBubble);
    return { messageDiv, chatBubble };
}
// ── Public API ───────────────────────────────────────────────────────────────
export function addBotMessage(chatMessages, text, _sources = [], isUpdate = false, targetMessage = null, spanId = null) {
    const wasScrolledToBottom = isScrolledToBottom(chatMessages);
    if (isUpdate && targetMessage) {
        const chatBubble = targetMessage.querySelector('.chat-bubble');
        if (chatBubble) {
            chatBubble.innerHTML = marked.parse(text);
        }
        if (spanId)
            targetMessage.dataset.spanId = spanId;
    }
    else {
        const { messageDiv } = createChatMessage('assistant', text);
        if (spanId)
            messageDiv.dataset.spanId = spanId;
        chatMessages.appendChild(messageDiv);
    }
    if (wasScrolledToBottom)
        scrollToBottom(chatMessages);
}
export function addUserMessage(chatMessages, text) {
    const { messageDiv } = createChatMessage('user', text);
    chatMessages.appendChild(messageDiv);
    scrollToBottom(chatMessages);
}
export function createFeedbackWidget(spanId, apiBaseUrl) {
    const feedbackUrl = `${apiBaseUrl}/api/feedback`;
    const widgetId = `feedback-${spanId}`;
    const wrapper = document.createElement('div');
    wrapper.id = widgetId;
    wrapper.className = 'chat-footer feedback-widget';
    const inner = document.createElement('div');
    inner.className = 'inline-flex items-center gap-1 mt-1 border border-base-300 rounded-lg px-2 py-0.5';
    const labels = [
        { value: 'helpful', emoji: '👍', title: 'Helpful' },
        { value: 'irrelevant', emoji: '👎', title: 'Irrelevant' },
        { value: 'incorrect', emoji: '⚠️', title: 'Incorrect' },
    ];
    labels.forEach(({ value, emoji, title }) => {
        const form = document.createElement('form');
        form.setAttribute('hx-post', feedbackUrl);
        form.setAttribute('hx-target', `#${widgetId}`);
        form.setAttribute('hx-swap', 'outerHTML');
        const hiddenSpanId = document.createElement('input');
        hiddenSpanId.type = 'hidden';
        hiddenSpanId.name = 'span_id';
        hiddenSpanId.value = spanId;
        const hiddenLabel = document.createElement('input');
        hiddenLabel.type = 'hidden';
        hiddenLabel.name = 'label';
        hiddenLabel.value = value;
        const button = document.createElement('button');
        button.type = 'submit';
        button.className = 'btn btn-ghost btn-xs opacity-50 hover:opacity-100 transition-opacity';
        button.title = title;
        button.textContent = emoji;
        form.appendChild(hiddenSpanId);
        form.appendChild(hiddenLabel);
        form.appendChild(button);
        inner.appendChild(form);
    });
    wrapper.appendChild(inner);
    window.htmx.process(wrapper);
    return wrapper;
}
